import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { useLang } from "@/context/LanguageContext";
import { cn } from "@/lib/utils";

function LangToggle({ className = "" }) {
  const { lang, setLang } = useLang();
  return (
    <div className={cn("font-ui text-[0.7rem] tracking-[0.18em] flex items-center gap-2", className)} data-testid="lang-toggle">
      {["es", "en"].map((code, i) => (
        <span key={code} className="flex items-center gap-2">
          <button
            onClick={() => setLang(code)}
            data-testid={`lang-${code}`}
            className={cn(
              "uppercase transition-colors duration-300",
              lang === code ? "text-latir-red" : "text-ink-mute hover:text-ink"
            )}
            aria-pressed={lang === code}
          >
            {code}
          </button>
          {i === 0 && <span className="text-line-strong">/</span>}
        </span>
      ))}
    </div>
  );
}

export default function Navbar() {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const links = [
    { to: "/", label: t("nav.home"), end: true },
    { to: "/sobre-latir", label: t("nav.recorrido") },
    { to: "/artistas", label: t("nav.artists") },
    { to: "/agenda", label: t("nav.agenda") },
    { to: "/reservas", label: t("nav.visitas") },
  ];

  // The navbar is always a light ivory banner — sitting elegantly over any
  // background. It gains a stronger border and slightly deeper opacity once
  // the user scrolls, but never turns dark.
  return (
    <header
      className={cn(
        "fixed top-0 inset-x-0 z-50 transition-all duration-500 ease-latir",
        "backdrop-blur-xl border-b",
        scrolled
          ? "bg-[rgba(248,245,240,0.94)] border-line py-4"
          : "bg-[rgba(248,245,240,0.82)] border-[rgba(0,0,0,0.06)] py-5"
      )}
      data-testid="navbar"
    >
      <div className="mx-auto max-w-[1500px] px-5 md:px-8 lg:px-10 flex items-center justify-between gap-8 lg:gap-12">
        <Link to="/" aria-label="Arteconciencia — Inicio" data-testid="nav-logo" className="shrink-0 min-w-0">
          <img
            src="/brand/arteconcienciasv-logo.webp"
            alt="Arteconciencia"
            className="w-auto h-[11px] md:h-[14px] lg:h-[17px] max-w-[85px] sm:max-w-[100px] md:max-w-none object-contain"
          />
        </Link>

        {/* desktop nav — activates only from lg (≥ 1024px) so it never crowds the logo. */}
        <nav className="hidden lg:flex items-center gap-6 xl:gap-9 2xl:gap-10">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`nav-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
              className={({ isActive }) =>
                cn(
                  "font-ui uppercase tracking-[0.16em] xl:tracking-[0.2em] text-[0.68rem] xl:text-[0.72rem] whitespace-nowrap transition-colors duration-300 link-underline",
                  isActive ? "text-latir-red" : "text-ink hover:text-latir-red"
                )
              }
            >
              {l.label}
            </NavLink>
          ))}
          <LangToggle className="ml-1 pl-5 xl:pl-8 border-l border-line" />
        </nav>

        {/* mobile / tablet trigger (< 1024 px) */}
        <div className="lg:hidden flex items-center gap-5">
          <LangToggle />
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button aria-label={t("nav.menu")} data-testid="mobile-menu-trigger" className="text-ink">
                <Menu strokeWidth={1.4} className="w-6 h-6" />
              </button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="w-[86%] sm:w-[420px] bg-paper border-l border-line p-0 [&>button]:hidden"
            >
              <div className="flex flex-col h-full px-7 py-7">
                <div className="flex items-center justify-between">
                  <img src="/brand/arteconcienciasv-logo.webp" alt="Arteconciencia" className="h-[15px] w-auto max-w-[115px] object-contain" />
                  <SheetClose asChild>
                    <button aria-label={t("nav.close")} data-testid="mobile-menu-close" className="text-ink">
                      <X strokeWidth={1.4} className="w-6 h-6" />
                    </button>
                  </SheetClose>
                </div>

                <nav className="mt-16 flex flex-col">
                  <AnimatePresence>
                    {links.map((l, i) => (
                      <motion.div
                        key={l.to}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.08 + i * 0.06, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                      >
                        <NavLink
                          to={l.to}
                          end={l.end}
                          data-testid={`mobile-nav-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
                          className={({ isActive }) =>
                            cn(
                              "block font-display text-[2.4rem] leading-[1.5] transition-colors duration-300",
                              isActive ? "text-latir-red" : "text-ink hover:text-latir-red"
                            )
                          }
                        >
                          {l.label}
                        </NavLink>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </nav>

                <div className="mt-auto pt-8 border-t border-line">
                  <p className="label-ui">{t("footer.venue")}</p>
                  <p className="label-ui mt-2 text-ink-mute/70">{t("sample.tag")}</p>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
